import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import ServiceBriefingPageShell from "../../components/service-briefings/ServiceBriefingPageShell";
import {
  SERVICE_BRIEFING_KINDS,
  SERVICE_BRIEFING_STATUSES,
  STATUS_COLOR,
  euro,
  formatDay,
  isOverdue,
  kindLabel,
  statusLabel,
} from "../../components/service-briefings/serviceBriefingLabels";
import { useGetUsersQuery } from "../../store/apis/usersApi";
import { useListServiceBriefingsQuery } from "../../store/apis/serviceBriefingApi";
import {
  ServiceBriefingKind,
  ServiceBriefingStatus,
} from "../../types/serviceBriefing";

type KindTab = ServiceBriefingKind | "ALL";

// Por defecto se esconde lo cerrado: lo que interesa es lo que falta por hacer.
const OPEN_STATUSES: ServiceBriefingStatus[] = [
  "DRAFT",
  "REQUESTED",
  "IN_PROGRESS",
];

/**
 * Listado de briefings de House of Stories, House of AI y House of Digital. La pestaña de tipo va
 * en la URL (?kind=) para que cada equipo pueda guardarse el enlace a la suya.
 */
const ServiceBriefings: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const kindParam = searchParams.get("kind") as ServiceBriefingKind | null;
  const kind: KindTab =
    kindParam && SERVICE_BRIEFING_KINDS.includes(kindParam) ? kindParam : "ALL";

  const [status, setStatus] = useState<ServiceBriefingStatus[]>(OPEN_STATUSES);
  const [assigneeUserId, setAssigneeUserId] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);

  // Cualquier cambio de filtro vuelve a la primera página.
  useEffect(() => {
    setPage(0);
  }, [kind, status, assigneeUserId, debouncedSearch, pageSize]);

  const { data, isFetching, isError } = useListServiceBriefingsQuery({
    kind: kind === "ALL" ? undefined : kind,
    status: status.length ? status : undefined,
    assigneeUserId: assigneeUserId || undefined,
    search: debouncedSearch || undefined,
    page: page + 1,
    pageSize,
  });
  const result = data?.data;
  const items = result?.items ?? [];

  const { data: usersData } = useGetUsersQuery();
  const userOptions = useMemo(
    () =>
      (usersData?.data ?? [])
        .filter((u) => u.active !== false && u.role !== "CLIENT")
        .map((u) => ({
          id: u.id,
          label:
            [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
            u.email,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "es")),
    [usersData]
  );

  const setKind = (next: KindTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === "ALL") params.delete("kind");
    else params.set("kind", next);
    setSearchParams(params, { replace: true });
  };

  return (
    <ServiceBriefingPageShell>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ md: "center" }}
        gap={2}
      >
        <Box>
          <Typography variant="h5">
            {t("serviceBriefings.list.title", "Briefings de servicios")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              "serviceBriefings.list.subtitle",
              "House of Stories, House of AI y House of Digital"
            )}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() =>
            navigate(
              kind === "ALL"
                ? "/service-briefings/new"
                : `/service-briefings/new?kind=${kind}`
            )
          }
        >
          {t("serviceBriefings.list.new", "Nuevo briefing")}
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${theme.palette.grey[200]}`,
          p: { xs: 2, md: 3 },
        }}
      >
        <Tabs
          value={kind}
          onChange={(_, next: KindTab) => setKind(next)}
          variant="scrollable"
          sx={{ mb: 2 }}
        >
          <Tab value="ALL" label={t("serviceBriefings.list.all", "Todos")} />
          {SERVICE_BRIEFING_KINDS.map((k) => (
            <Tab key={k} value={k} label={kindLabel(t, k)} />
          ))}
        </Tabs>

        <Stack direction={{ xs: "column", md: "row" }} gap={2} mb={2}>
          <TextField
            size="small"
            sx={{ flex: 2 }}
            label={t("serviceBriefings.list.search", "Buscar")}
            placeholder={t(
              "serviceBriefings.list.searchPlaceholder",
              "Nombre, campaña, marca o cliente"
            )}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FormControl size="small" sx={{ flex: 2 }}>
            <InputLabel>
              {t("serviceBriefings.fields.status", "Estado")}
            </InputLabel>
            <Select
              multiple
              value={status}
              onChange={(e) =>
                setStatus(
                  typeof e.target.value === "string"
                    ? (e.target.value.split(",") as ServiceBriefingStatus[])
                    : e.target.value
                )
              }
              input={
                <OutlinedInput
                  label={t("serviceBriefings.fields.status", "Estado")}
                />
              }
              renderValue={(selected) =>
                selected.map((s) => statusLabel(t, s)).join(", ")
              }
            >
              {SERVICE_BRIEFING_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {statusLabel(t, s)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            size="small"
            sx={{ flex: 2 }}
            options={userOptions}
            value={userOptions.find((o) => o.id === assigneeUserId) ?? null}
            onChange={(_, option) => setAssigneeUserId(option?.id ?? "")}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("serviceBriefings.fields.assignee", "Asignado a")}
              />
            )}
          />
        </Stack>

        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t(
              "serviceBriefings.list.loadFailed",
              "No se pudieron cargar los briefings"
            )}
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("serviceBriefings.form.name", "Nombre")}
                </TableCell>
                <TableCell>
                  {t("serviceBriefings.fields.client", "Cliente")}
                </TableCell>
                {kind === "ALL" && (
                  <TableCell>
                    {t("serviceBriefings.fields.kind", "Tipo")}
                  </TableCell>
                )}
                <TableCell>
                  {t("serviceBriefings.fields.requestDateShort", "Solicitud")}
                </TableCell>
                <TableCell>
                  {t("serviceBriefings.fields.deliveryDateShort", "Entrega")}
                </TableCell>
                <TableCell>
                  {t("serviceBriefings.fields.assignee", "Asignado a")}
                </TableCell>
                <TableCell>
                  {t("serviceBriefings.fields.status", "Estado")}
                </TableCell>
                <TableCell align="right">
                  {t("serviceBriefings.lines.totalCost", "Coste")}
                </TableCell>
                <TableCell align="right">
                  {t("serviceBriefings.lines.totalPrice", "Precio")}
                </TableCell>
                <TableCell align="right">
                  {t("serviceBriefings.lines.margin", "Margen")}
                </TableCell>
                <TableCell align="right">
                  {t("serviceBriefings.assets.title", "Assets")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              )}
              {!isFetching && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        "serviceBriefings.list.empty",
                        "No hay briefings con estos filtros."
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {items.map((item) => {
                const overdue = isOverdue(item.deliveryDate, item.status);
                return (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/service-briefings/${item.id}`)}
                  >
                    <TableCell sx={{ maxWidth: 320, wordBreak: "break-word" }}>
                      {item.name}
                    </TableCell>
                    <TableCell>{item.companyName}</TableCell>
                    {kind === "ALL" && (
                      <TableCell>{kindLabel(t, item.kind)}</TableCell>
                    )}
                    <TableCell>{formatDay(item.requestDate)}</TableCell>
                    <TableCell
                      sx={{
                        color: overdue ? "error.main" : undefined,
                        fontWeight: overdue ? 600 : undefined,
                      }}
                    >
                      {formatDay(item.deliveryDate)}
                    </TableCell>
                    <TableCell>{item.assignee?.name ?? "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={STATUS_COLOR[item.status]}
                        label={statusLabel(t, item.status)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {euro.format(item.totals.totalCost)}
                    </TableCell>
                    <TableCell align="right">
                      {euro.format(item.totals.totalPrice)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color:
                          item.totals.margin < 0 ? "error.main" : undefined,
                      }}
                    >
                      {euro.format(item.totals.margin)}
                    </TableCell>
                    <TableCell align="right">{item.assetsCount}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={result?.total ?? 0}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
          rowsPerPageOptions={[20, 50, 100]}
          labelRowsPerPage={t("serviceBriefings.list.rowsPerPage", "Filas")}
        />
      </Paper>
    </ServiceBriefingPageShell>
  );
};

export default ServiceBriefings;
